import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Menu,
  MenuIcon,
  Stack,
  Typography,
} from '@cmg/design-system';
import { Fragment, useCallback, useState } from 'react';

import { useOfferingSidePanel_MobileActionPanelQuery } from './graphql';
import { useSidePanelBuySideNavItems } from './hooks/useSidePanelBuySideNavItems';
import { useSidePanelSellSideNavItems } from './hooks/useSidePanelSellSideNavItems';
import { useIdentifyOrderBookPersona } from './order-book/hooks/useIdentifyOrderBookPersona';

type MobileActionPanelMenuProps = Readonly<{
  offeringId: string;
  baseSidePanelUrl: string;
}>;

export function MobileActionPanelMenu({
  offeringId,
  baseSidePanelUrl,
}: MobileActionPanelMenuProps) {
  const { data } = useOfferingSidePanel_MobileActionPanelQuery({ variables: { offeringId } });
  const [offering] = data?.publishedOfferings?.items ?? [];
  const { isSellSide } = useIdentifyOrderBookPersona({ offeringId });

  const sellSideNavItems = useSidePanelSellSideNavItems({ offeringId, baseSidePanelUrl });
  const buySideNavItems = useSidePanelBuySideNavItems({
    baseSidePanelUrl,
    isExecutingOnPlatform: offering?.isExecutingOnPlatform,
    isLinkedOffering: !!offering?.dataLabFull?.linkedOfferingId,
  });

  const navItems = isSellSide ? sellSideNavItems : buySideNavItems;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);
  return (
    <Stack direction="row" alignItems="center" gap={2}>
      <IconButton
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label="open mobile action panel"
        onClick={handleClick}
      >
        <MenuIcon />
      </IconButton>
      <Menu id="basic-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
        <List disablePadding>
          {navItems?.map(navItem => {
            if ('items' in navItem) {
              return (
                <Fragment key={navItem.id}>
                  <ListSubheader>{navItem.label}</ListSubheader>
                  <List disablePadding>
                    {navItem.items.map(subItem => {
                      return (
                        <ListItem disablePadding key={subItem.id}>
                          <ListItemButton
                            onClick={handleClose}
                            href={subItem.href}
                            selected={subItem.selected}
                          >
                            <ListItemText sx={{ paddingLeft: 2 }}>{subItem.label}</ListItemText>
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Fragment>
              );
            }
            return (
              <ListItem key={navItem.id} disablePadding>
                <ListItemButton
                  onClick={handleClose}
                  href={navItem.href}
                  selected={navItem.selected}
                >
                  {navItem.label}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Menu>
      <Stack>
        <Typography
          variant="caption"
          component="h2"
          id="offering-side-panel-header"
          color="text.secondary"
        />
        <Typography variant="subtitle1" component="h2">
          {offering?.issuer.name}
        </Typography>
      </Stack>
    </Stack>
  );
}
